/**
 * Image manipulation utils.
 *
 * This code is a rewrite from:
 * https://github.com/mtschirs/js-objectdetect implementation
 * @author Martin Tschirsich / http://www.tu-darmstadt.de/~m_t
 * and https://github.com/inspirit/jsfeat implementation
 * @author Eugene Zatepyakin / http://inspirit.ru/
 *
 * @copyright Realeyes OU. All rights reserved.
 */


/**
 * Buffer for histogram calculation.
 *
 * @type {Uint32Array}
 */
const HIST = new Uint32Array(256);

/**
 * Grayscale conversion coefficients.
 *
 * @type {number}
 */
const GRAYSCALE_COEF_R = 4899;
const GRAYSCALE_COEF_G = 9617;
const GRAYSCALE_COEF_B = 1868;

/**
 * Canvas workspace for image resizing.
 */
const workCanvas = document.createElement('canvas');
const workCtx = workCanvas.getContext('2d');

export default {
    /**
     * Resizes the image.
     *
     * @param {ArrayBuffer} image Source image
     * @param {number} oldWidth Old width
     * @param {number} oldHeight Old height
     * @param {number} newWidth New width
     * @param {number} newHeight New height
     * @return {ArrayBuffer}
     */
    resize(image, oldWidth, oldHeight, newWidth, newHeight) {
        workCanvas.width = oldWidth;
        workCanvas.height = oldHeight;
        const canvasBuffer = workCtx.getImageData(0, 0, oldWidth, oldHeight);
        const canvasBufferView = new Uint32Array(canvasBuffer.data.buffer);
        const imageView = new Uint32Array(image);
        for (let i = 0; i < canvasBufferView.length; i++) {
            canvasBufferView[i] = imageView[i];
        }
        workCtx.putImageData(canvasBuffer, 0, 0);
        workCtx.scale(newWidth / oldWidth, newHeight / oldHeight);
        workCtx.drawImage(workCtx.canvas, 0, 0);

        return workCtx.getImageData(0, 0, newWidth, newHeight).data.buffer;
    },
    /**
     * Converts RGBA image to grayscale.
     *
     * @param {ArrayBuffer} src Source buffer with 4-channel RGBA image
     * @param {ArrayBuffer} dst Destination buffer for 8bit 1-channel grayscale image
     * @return {undefined}
     */
    grayscale(src, dst) {
        const srcView = new Uint8Array(src);
        const dstView = new Uint8Array(dst);

        for (let i = 0; i < srcView.length; i += 4) {
            dstView[i >> 2] = ((srcView[i] * GRAYSCALE_COEF_R)
                               + (srcView[i + 1] * GRAYSCALE_COEF_G)
                               + (srcView[i + 2] * GRAYSCALE_COEF_B) + 8192) >> 14;
        }
    },
    /**
     * Normalizes the brightness and increases the contrast of the image.
     * TODO: we should measure performance and make it possible to increase sampling step size if needed.
     *
     * @param {ArrayBuffer} src Source buffer with a 8bit 1-channel grayscale image
     * @param {ArrayBuffer} dst Destination buffer for a normalized image
     * @return {undefined}
     */
    equalizeHistogram(src, dst) {
        HIST.fill(0);
        const srcView = new Uint8Array(src);
        const dstView = new Uint8Array(dst);

        for (let i = 0; i < srcView.length; i++) {
            ++HIST[srcView[i]];
        }

        let prev = HIST[0];
        for (let i = 1; i < 256; i++) {
            prev = HIST[i] += prev;
        }

        const norm = 255 / srcView.length;
        for (let i = 0; i < srcView.length; i++) {
            dstView[i] = ((HIST[srcView[i]] * norm) + 0.5) | 0;
        }
    },

    /**
     * Computes the integral and squared integral images.
     *
     * Each destination should be 1 pixel larger than source in both dimensions.
     *
     * @param {ArrayBuffer} src Source buffer with a 8bit 1-channel grayscale image
     * @param {number} width Image width
     * @param {number} height Image height
     * @param {ArrayBuffer} dstSum Destination buffer for an integral image
     * @param {ArrayBuffer} dstSqSum Destination buffer for a squared integral image
     * @return {undefined}
     */
    computeIntegralImage(src, width, height, dstSum, dstSqSum) {
        const srcView = new Uint8Array(src);
        const dstSumView = new Uint32Array(dstSum);
        const dstSqSumView = new Uint32Array(dstSqSum);
        dstSumView.fill(0);
        dstSqSumView.fill(0);

        for (let x = 1; x <= width; x++) {
            for (let y = 1, i = x, colSum = 0, colSqSum = 0; y <= height; y++) {
                const val = srcView[i - y];
                colSum += val;
                colSqSum += val * val;
                i += width + 1;
                dstSumView[i] = dstSumView[i - 1] + colSum;
                dstSqSumView[i] = dstSqSumView[i - 1] + colSqSum;
            }
        }
    },
};
